import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import {
  usePrepareDisclosuresWizard,
  useRegisterStepGate,
  useRegisterStepSubmit,
} from '../../../hooks';
import { WizardStep } from '../../../PrepareDisclosuresWizard.constants';
import { summarizeDelivery } from '../BorrowersDeliveryStep/borrowersDelivery.utils';

import AttestationsCard from './components/AttestationsCard';
import SendStatusNote from './components/SendStatusNote';
import SendSummaryCard from './components/SendSummaryCard';
import { useDisclosureSend } from './hooks';
import { SEND_COPY, SendStatus } from './sendOrchestration.constants';
import { areAttestationsComplete } from './sendOrchestration.utils';
import { headerStyles, layoutStyles, subtitleStyles } from './SendStep.styles';
import { SendStepTestIds } from './SendStep.testIds';

const STATUS_NOTE_COPY = {
  [SendStatus.BLOCKED_READINESS]: { message: SEND_COPY.readinessBlocked, isError: false },
  [SendStatus.UNAVAILABLE]: { message: SEND_COPY.unavailable, isError: false },
  [SendStatus.NO_SEND_ACTION]: { message: SEND_COPY.noSendAction, isError: true },
};

const buildErrorNote = errorMessage => ({
  message: errorMessage ? `${SEND_COPY.errorLead} ${errorMessage}` : SEND_COPY.error,
  isError: true,
});

// The package is always delivered electronically. Mail is a workflow the broker
// records after sending (Mark as mailed), so the summary reflects the honored
// electronic send plus any planned mail follow-up.
const buildDeliveryValue = ({ mailCount }) =>
  mailCount ? `Electronic · ${mailCount} to mail & record after sending` : 'Electronic (e-sign)';

const SendStep = () => {
  const { previewResult, borrowersDelivery } = usePrepareDisclosuresWizard();
  const [attestations, setAttestations] = useState({});
  const { status, send, errorMessage, sendAction, isResolvingSendAction } =
    useDisclosureSend(attestations);

  const isSending = status === SendStatus.SENDING;
  const attestationsComplete = areAttestationsComplete(attestations);
  const isSendActionBlocked = !isResolvingSendAction && !sendAction;

  useRegisterStepGate(
    WizardStep.SEND,
    attestationsComplete && !isSending && !isResolvingSendAction && !!sendAction,
  );
  useRegisterStepSubmit(WizardStep.SEND, send);

  const handleToggle = useCallback(
    (type, checked) => setAttestations(prev => ({ ...prev, [type]: checked })),
    [],
  );

  const summaryRows = useMemo(() => {
    const delivery = summarizeDelivery(borrowersDelivery);
    const actionValue = isResolvingSendAction
      ? SEND_COPY.resolvingAction
      : (sendAction?.name ?? SEND_COPY.noSendActionValue);

    return [
      { label: 'Package', value: previewResult?.formName ?? 'Initial Loan Estimate' },
      { label: SEND_COPY.actionLabel, value: actionValue },
      { label: 'Signers', value: String(delivery.signerCount) },
      { label: 'Delivery', value: buildDeliveryValue(delivery) },
    ];
  }, [borrowersDelivery, previewResult, sendAction, isResolvingSendAction]);

  const statusNote =
    status === SendStatus.ERROR
      ? buildErrorNote(errorMessage)
      : (STATUS_NOTE_COPY[isSendActionBlocked ? SendStatus.NO_SEND_ACTION : status] ?? null);

  return (
    <Box data-testid={SendStepTestIds.ROOT} sx={layoutStyles}>
      <Box sx={headerStyles}>
        <Typography variant="bold14">{SEND_COPY.title}</Typography>
        <Typography sx={subtitleStyles} variant="regular12">
          {SEND_COPY.subtitle}
        </Typography>
      </Box>
      <SendSummaryCard rows={summaryRows} />
      <AttestationsCard attestations={attestations} disabled={isSending} onToggle={handleToggle} />
      {statusNote && <SendStatusNote isError={statusNote.isError} message={statusNote.message} />}
    </Box>
  );
};

export default memo(SendStep);
